import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  HandbookLinkFormSchema,
  HandbookLinkFormValues,
} from '@/pages/setup/link_handlers/handbook_link/handbook_links.schema.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdateAccessHandbookLink } from '@/queries/help_links/hooks.ts'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { Button } from '@/app/common/components/button/Button'
import { EditIconGeneral } from '@/common/icons/general.tsx'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'

interface Props {
  linkId: number
  currentLink: string
  onEditHandbookLinkSuccess: () => void
  handbookLinks: { link: string; id: number }[]
}

const EditLinkPopup = (props: Props) => {
  const { linkId, currentLink, onEditHandbookLinkSuccess, handbookLinks } =
    props
  const { toast } = useToast()

  const [open, setOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HandbookLinkFormValues>({
    resolver: zodResolver(HandbookLinkFormSchema),
    mode: 'onBlur',
    defaultValues: {
      link:
        handbookLinks.find((link) => link.id === linkId)?.link ||
        currentLink ||
        '',
    },
  })

  const updateHandbookLink = useUpdateAccessHandbookLink({
    onSuccess: () => {
      toast({
        title: 'Wallet Handbook Link Updated',
        description: 'The Wallet handbook link has been updated successfully.',
        variant: 'default',
      })
      onEditHandbookLinkSuccess()
      setOpen(false)
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: `${error}`,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: HandbookLinkFormValues) => {
    updateHandbookLink.mutate({
      id: linkId,
      data: {
        link: data.link,
      },
    })
  }

  const handleClose = () => {
    setOpen(false)
    reset({ link: currentLink })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value)
      }}
    >
      <DialogTrigger>
        <Button
          onClick={(event) => {
            setOpen(true)
            event.stopPropagation()
          }}
          variant={'outline'}
        >
          <EditIconGeneral />
        </Button>
      </DialogTrigger>
      <DialogContent onClick={(event) => event.stopPropagation()}>
        <h2 className="font-medium">Edit Handbook Link</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
          <div>
            <label>Handbook Link</label>
            <Input
              type="text"
              {...register('link')}
              aria-invalid={errors.link ? 'true' : 'false'}
            />
            {errors.link && (
              <p className="text-sm text-red-500 mt-1">{errors.link.message}</p>
            )}
          </div>
          <div className="flex space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <div>
              <SubmitButton
                buttonTxt="Save changes"
                status={updateHandbookLink.status}
              />
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default EditLinkPopup
